/**
 * Activity-feed slice (PX-W6.3, TASK-EDD04F2F) — ported from xelerate-ai's
 * `features/activity-feed/slices/activityFeedSlice.ts` (TASK-8CF4A653) onto
 * the px wire, with the px deltas: a single `type` filter (the px endpoint
 * takes one), and the realtime row arriving as the payload-carrying
 * `realtime/invalidateActivity` nonce action rather than a direct dispatch.
 *
 * The realtime model is REFETCH-ONLY (INV-4/INV-5): a bus `activity.created`
 * whose payload `projectId` matches the project on screen bumps the
 * invalidation nonce; the mounted panel refetches PAGE 1 when it moves. Bus
 * payloads are NEVER delta-merged into `entries`, and another project's
 * activity never disturbs the feed (AC-5 negative).
 *
 * Every async case is guarded BOTH at dispatch time (`condition`) and at
 * resolution time (project still on screen), and next-page resolutions carry
 * a `pagedFrom` epoch echo dropped unless the feed still holds that cursor —
 * all carried over verbatim from the hardened reference.
 */

import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { isApiError } from '@/services'
import { activityFeedService } from '../services/activityFeedService.http'
import { FEED_PAGE_SIZE, type ActivityFeedEntry, type ActivityFeedFilters, type ActivityFeedResponse } from '../types'

/** Neutral filter set — a module constant so resets keep a stable identity. */
export const INITIAL_FEED_FILTERS: ActivityFeedFilters = {
  type: null,
  actorId: null
}

/** Whether any server-side filter is active (drives the empty-state copy). */
export function hasActiveFeedFilters(filters: ActivityFeedFilters): boolean {
  return filters.type !== null || filters.actorId !== null
}

/** Which feed thunk a fetch failure came from — drives the panel's Retry target. */
type ActivityFeedErrorSource = 'firstPage' | 'nextPage'

/** A fetch failure: message + source travel as ONE value. */
export interface ActivityFeedError {
  message: string
  source: ActivityFeedErrorSource
}

export interface ActivityFeedState {
  /** Project slug currently on screen (null = feed not mounted). */
  projectId: string | null
  filters: ActivityFeedFilters
  /** Loaded rows, newest first, across all fetched pages. */
  entries: ActivityFeedEntry[]
  /** Cursor of the last loaded row; null when no more pages exist. */
  nextCursor: string | null
  loading: boolean
  loadingMore: boolean
  error: ActivityFeedError | null
  /** Bumped when the on-screen project's activity changed; the panel refetches page 1. */
  invalidationNonce: number
  /**
   * Batch contract C-3 — the requestId-scoped in-flight marker for the
   * FIRST-PAGE fetch. Set on `pending`, cleared on `fulfilled`/`rejected`
   * (matched by `requestId`), and deliberately left untouched by
   * `feedProjectSet`/`feedClosed`: StrictMode runs this panel's mount effect
   * cleanup (`feedClosed` then `feedProjectSet` again) BETWEEN the fetch
   * effect's two runs, and either reducer resetting an in-flight flag let the
   * second run's `condition` pass a second time — the double
   * `GET …/activity?limit=50` (deferred finding c1-2).
   */
  firstPageInFlight: FirstPageKey & { requestId: string } | null
  /**
   * TASK-7922504E (F1) — the (project, filters, nonce) of the last first page
   * that SETTLED successfully, recorded from the in-flight record (its START
   * nonce). Cleared by `feedProjectSet`, never set on a rejection, left alone
   * by `feedClosed`.
   */
  firstPageLoaded: FirstPageKey | null
}

/** What makes two first-page loads the same load. */
export interface FirstPageKey {
  projectId: string
  filtersKey: string
  /** The `invalidationNonce` the load started at. */
  nonce: number
}

const initialState: ActivityFeedState = {
  projectId: null,
  filters: INITIAL_FEED_FILTERS,
  entries: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  error: null,
  invalidationNonce: 0,
  firstPageInFlight: null,
  firstPageLoaded: null
}

/** The condition/pending/fulfilled matching key for a first-page fetch's filter set. */
const filtersKeyOf = (filters: ActivityFeedFilters): string => JSON.stringify(filters)

/** Whether a recorded first-page key names exactly the load the state asks for now. */
const isSameFirstPage = (key: FirstPageKey | null, state: ActivityFeedState, projectId: string): boolean =>
  key !== null &&
  key.projectId === projectId &&
  key.filtersKey === filtersKeyOf(state.filters) &&
  key.nonce === state.invalidationNonce

/** Argument shared by both feed thunks. */
interface FeedThunkArgs {
  orgId: string
  projectId: string
}

/**
 * The thunks carry NO `state` override: the pre-typed AppDispatch would
 * reject an AsyncThunkAction narrower than RootState (the `sessionSlice`
 * precedent — `getState()` is cast locally instead). Everything the guards
 * touch is `state.activityFeed`.
 */
function feedStateOf(getState: () => unknown): ActivityFeedState {
  return (getState() as { activityFeed: ActivityFeedState }).activityFeed
}

function feedErrorMessage(err: unknown): string {
  if (isApiError(err) || err instanceof Error) return err.message
  return 'Failed to load activity.'
}

/** Resolution-time staleness guard: the action must be for the project on screen. */
function isForProjectOnScreen(
  state: ActivityFeedState,
  action: { meta: { arg: FeedThunkArgs } }
): boolean {
  return action.meta.arg.projectId === state.projectId
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchActivityFeedFirstPage = createAsyncThunk<
  ActivityFeedResponse,
  FeedThunkArgs,
  { rejectValue: string }
>(
  'activityFeed/fetchFirstPage',
  async ({ orgId, projectId }, { getState, rejectWithValue }) => {
    const { filters } = feedStateOf(getState)
    try {
      return await activityFeedService.getFeed({
        orgId,
        projectId,
        limit: FEED_PAGE_SIZE,
        type: filters.type,
        actorId: filters.actorId
      })
    } catch (err) {
      return rejectWithValue(feedErrorMessage(err))
    }
  },
  {
    // Three vetoes, in order:
    //  1. on screen — the project must be the one the feed holds;
    //  2. in flight — a first page for the SAME project + filter set is
    //     already loading (batch contract C-3). The marker survives
    //     `feedProjectSet`/`feedClosed`, so a StrictMode re-run of the mount
    //     effect cannot slip a second request past this condition;
    //  3. already loaded — a first page for the same project, filter set AND
    //     nonce has already settled (TASK-7922504E F1: the intermittent second
    //     identical request when the panel's load effect re-runs after the
    //     first page landed).
    // A realtime frame landing WHILE a first page is in flight is NOT followed
    // up after it settles (Agon, 2026-09-23): the loaded marker keeps the
    // START nonce, so the next change of any dependency loads again, but
    // nothing re-dispatches on its own. This slice deliberately does not
    // mirror fetchCache's `markServerChanged` + follow-up listener
    // (TASK-3D6DB8B6, batch contract C-4) — the feed is a panel-owned list
    // that never went through fetchCache.
    condition: ({ projectId }, { getState }) => {
      const state = feedStateOf(getState)
      if (state.projectId !== projectId) return false
      const inFlight = state.firstPageInFlight
      if (inFlight?.projectId === projectId && inFlight.filtersKey === filtersKeyOf(state.filters)) return false
      return !isSameFirstPage(state.firstPageLoaded, state, projectId)
    }
  }
)

/** Success payload of a next-page fetch, echoing the cursor it paged FROM. */
interface NextPageSuccess extends ActivityFeedResponse {
  pagedFrom: string | null
}

/** Rejection payload of a next-page fetch, with the same `pagedFrom` echo. */
interface NextPageRejection {
  message: string
  pagedFrom: string | null
}

export const fetchActivityFeedNextPage = createAsyncThunk<
  NextPageSuccess,
  FeedThunkArgs,
  { rejectValue: NextPageRejection }
>(
  'activityFeed/fetchNextPage',
  async ({ orgId, projectId }, { getState, rejectWithValue }) => {
    const { nextCursor, filters } = feedStateOf(getState)
    try {
      const response = await activityFeedService.getFeed({
        orgId,
        projectId,
        limit: FEED_PAGE_SIZE,
        before: nextCursor,
        type: filters.type,
        actorId: filters.actorId
      })
      return { ...response, pagedFrom: nextCursor }
    } catch (err) {
      return rejectWithValue({ message: feedErrorMessage(err), pagedFrom: nextCursor })
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const s = feedStateOf(getState)
      return s.projectId === projectId && s.nextCursor !== null
    }
  }
)

// ---------------------------------------------------------------------------
// Bus frame alias — SAME type as the realtime nonce, payload-carrying
// ---------------------------------------------------------------------------

/**
 * Typed alias of `realtime/invalidateActivity` carrying the frame's
 * `projectId` (null when the row had no project tuple). The realtime slice's
 * own reducer bumps its nonce (payload ignored); this slice applies the
 * on-screen guard. A null `projectId` bumps nothing — user-level rows never
 * disturb a project feed.
 */
export const activityBusFrame = createAction<{ projectId: string | null }>(
  'realtime/invalidateActivity'
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const activityFeedSlice = createSlice({
  name: 'activityFeed',
  initialState,
  reducers: {
    /** Mount / project switch: reset every loaded bit of state. */
    feedProjectSet(state, action: { payload: string }) {
      state.projectId = action.payload
      state.filters = INITIAL_FEED_FILTERS
      state.entries = []
      state.nextCursor = null
      state.loading = false
      state.loadingMore = false
      state.error = null
      state.invalidationNonce = 0
      state.firstPageLoaded = null
    },
    /** Replace the filter set (the panel refetches page 1 on change). */
    feedFiltersSet(state, action: { payload: ActivityFeedFilters }) {
      state.filters = action.payload
    },
    /**
     * Realtime invalidation — bump the nonce ONLY when the event's project
     * is the one on screen (AC-5 negative: another project's activity, and
     * rows with no project tuple at all, never disturb this feed).
     */
    feedInvalidated(state, action: { payload: { projectId: string } }) {
      if (action.payload.projectId === state.projectId) {
        state.invalidationNonce += 1
      }
    },
    /** Leave the workspace. */
    feedClosed(state) {
      state.projectId = null
      state.entries = []
      state.nextCursor = null
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityFeedFirstPage.pending, (state, action) => {
        // Batch contract C-3: recorded BEFORE the on-screen guard, so it
        // always reflects the request a passing `condition` actually
        // started — `feedProjectSet`/`feedClosed` never clear it.
        state.firstPageInFlight = {
          requestId: action.meta.requestId,
          projectId: action.meta.arg.projectId,
          filtersKey: filtersKeyOf(state.filters),
          nonce: state.invalidationNonce
        }
        if (!isForProjectOnScreen(state, action)) return
        state.loading = true
        state.error = null
      })
      .addCase(fetchActivityFeedFirstPage.fulfilled, (state, action) => {
        const inFlight = state.firstPageInFlight
        const ownInFlight = inFlight?.requestId === action.meta.requestId ? inFlight : null
        if (ownInFlight !== null) {
          state.firstPageInFlight = null
        }
        if (!isForProjectOnScreen(state, action)) return
        if (ownInFlight !== null) {
          const { projectId, filtersKey, nonce } = ownInFlight
          state.firstPageLoaded = { projectId, filtersKey, nonce }
        }
        state.loading = false
        state.entries = action.payload.items
        state.nextCursor = action.payload.nextCursor
        state.error = null
      })
      .addCase(fetchActivityFeedFirstPage.rejected, (state, action) => {
        if (state.firstPageInFlight?.requestId === action.meta.requestId) {
          state.firstPageInFlight = null
        }
        if (!isForProjectOnScreen(state, action)) return
        if (action.meta.condition) return
        state.loading = false
        state.error = { message: action.payload ?? 'Failed to load activity.', source: 'firstPage' }
      })
      .addCase(fetchActivityFeedNextPage.pending, (state, action) => {
        if (!isForProjectOnScreen(state, action)) return
        state.loadingMore = true
        state.error = null
      })
      .addCase(fetchActivityFeedNextPage.fulfilled, (state, action) => {
        if (!isForProjectOnScreen(state, action)) return
        state.loadingMore = false
        // Epoch guard: this fetch paged from a cursor the feed no longer
        // holds — page 1 was replaced while it was in flight. Appending now
        // would mix filter sets and duplicate entry keys.
        if (action.payload.pagedFrom !== state.nextCursor) return
        state.entries.push(...action.payload.items)
        state.nextCursor = action.payload.nextCursor
        state.error = null
      })
      .addCase(fetchActivityFeedNextPage.rejected, (state, action) => {
        if (!isForProjectOnScreen(state, action)) return
        if (action.meta.condition) return
        state.loadingMore = false
        if (action.payload?.pagedFrom !== state.nextCursor) return
        state.error = { message: action.payload.message, source: 'nextPage' }
      })
      .addCase(activityBusFrame, (state, action) => {
        const { projectId } = action.payload
        if (projectId !== null && projectId === state.projectId) {
          state.invalidationNonce += 1
        }
      })
  }
})

export const { feedProjectSet, feedFiltersSet, feedInvalidated, feedClosed } =
  activityFeedSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

const selectActivityFeedSlice = (state: { activityFeed: ActivityFeedState }) => state.activityFeed

/**
 * The loaded rows. Defaulted at the SELECTOR, so every consumer is guarded at
 * once: an absent `entries` array threw in three separate places (the actor
 * picker, the type filter, the list) and took the whole workspace down with it.
 */
export const selectActivityFeedEntries = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.entries ?? EMPTY_ENTRIES
export const selectActivityFeedLoading = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.loading
export const selectActivityFeedLoadingMore = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.loadingMore
export const selectActivityFeedError = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.error
export const selectActivityFeedHasMore = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.nextCursor !== null
export const selectActivityFeedNonce = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.invalidationNonce
export const selectActivityFeedFilters = (state: { activityFeed: ActivityFeedState }) =>
  state.activityFeed.filters

/** A STABLE empty array: a fresh `[]` per call would defeat every memo below. */
const EMPTY_ENTRIES: ActivityFeedState['entries'] = []

/** Distinct authors of the loaded rows — the actor picker's options (memoised). */
export const selectActivityFeedAuthors = createSelector(selectActivityFeedSlice, (feed) => {
  const seen = new Set<string>()
  const authors: { id: string; name: string }[] = []
  // Destructured with a default: a feed state whose `entries` is absent (a
  // malformed or partially-hydrated payload) made this selector throw inside
  // reselect, taking the whole workspace down rather than rendering an empty
  // actor picker.
  const { entries = [] } = feed
  for (const entry of entries) {
    const { author } = entry
    if (!author || typeof author.id !== 'string' || seen.has(author.id)) continue
    seen.add(author.id)
    authors.push({ id: author.id, name: author.name ?? author.id })
  }
  return authors
})
